import { useState, type ReactNode } from 'react';
import type { VerticalConfig } from '@garnet/config';
import {
  Button,
  ConfirmButton,
  DepthGradient,
  Field,
  Icon,
  Input,
  OCRConfidence,
  QualState,
  RenewalTimeline,
  ScreenHeader,
  Select,
  StateBox,
  TierBadge,
  WeakestLinkBanner,
} from '@garnet/ui';
import type { Professional, VerticalData } from '../data/types.js';
import { COUNTRY, COUNTRY_KEYS, carrierState, proCost, toDepth } from '../data/shape.js';
import { StatePill } from '../components/StatePill.js';
import type { Route } from '../nav.js';

/* Garnet · Diligence flow — the spine.
   Start → Documents → OCR review → Verify → Professional → Record → Decision.
   A vertical is a CONFIG plus a dataset (PRD §38): the identity label below reads from
   @garnet/config; every other string comes from the dataset's copy. */

type StepKey = 'start' | 'docs' | 'ocr' | 'verify' | 'professional' | 'record' | 'decision';
type DecisionKey = 'qualify' | 'conditional' | 'block';

const STEPS: ReadonlyArray<{ k: StepKey; n: string; sub: string }> = [
  { k: 'start', n: 'Start', sub: 'supplier & part' },
  { k: 'docs', n: 'Documents', sub: 'upload' },
  { k: 'ocr', n: 'OCR review', sub: 'confirm fields' },
  { k: 'verify', n: 'Verify', sub: 'run pipeline' },
  { k: 'professional', n: 'Professional', sub: 'request & cost' },
  { k: 'record', n: 'Record', sub: 'composited' },
  { k: 'decision', n: 'Decision', sub: 'qualify / block' },
];

function Rail({ idx, maxReached, go }: { idx: number; maxReached: number; go: (i: number) => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'stretch', border: '1px solid var(--rule)', background: 'var(--panel)', marginBottom: 22, overflowX: 'auto' }}>
      {STEPS.map((s, i) => {
        const done = i < idx;
        const active = i === idx;
        const reachable = i <= maxReached;
        return (
          <button
            key={s.k}
            type="button"
            onClick={() => reachable && go(i)}
            disabled={!reachable}
            style={{
              flex: 1, minWidth: 116, textAlign: 'left', padding: '12px 14px', cursor: reachable ? 'pointer' : 'default',
              border: '0', borderRight: i < STEPS.length - 1 ? '1px solid var(--rule)' : 0,
              background: active ? 'var(--ink)' : done ? 'var(--rule-soft)' : 'var(--panel)',
              color: active ? 'var(--paper)' : reachable ? 'var(--ink)' : 'var(--muted)', position: 'relative',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{
                fontFamily: 'var(--mono)', fontSize: 10.5, fontWeight: 600,
                width: 18, height: 18, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid ' + (active ? 'var(--paper)' : done ? 'var(--verified)' : 'var(--rule)'),
                background: done ? 'var(--verified)' : 'transparent', color: done ? '#fff' : 'inherit',
              }}>
                {done ? <Icon name="check" size={11} /> : i + 1}
              </span>
              <span style={{ fontFamily: 'var(--display)', fontWeight: 600, fontSize: 13.5 }}>{s.n}</span>
            </div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, marginTop: 4, marginLeft: 25, opacity: active ? 0.8 : 0.65 }}>{s.sub}</div>
          </button>
        );
      })}
    </div>
  );
}

function Card({ children, max }: { children: ReactNode; max?: number }) {
  return <div className="panel" style={{ padding: '24px 26px', maxWidth: max ?? 760, margin: '0 auto', width: '100%' }}>{children}</div>;
}

function StepTitle({ n, title, hint }: { n: string; title: string; hint?: string }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div className="eyebrow-sm">Step {n}</div>
      <h2 className="display" style={{ fontSize: '1.4rem', fontWeight: 600, margin: '4px 0 0', letterSpacing: '-.01em' }}>{title}</h2>
      {hint && <p style={{ color: 'var(--muted)', fontSize: 13.5, marginTop: 7, maxWidth: '58ch' }}>{hint}</p>}
    </div>
  );
}

export function Flow({ data, config, go }: { data: VerticalData; config: VerticalConfig; go: (r: Route) => void }) {
  const { hero, inbox } = data;
  const copy = data.copy;

  const heroSupplier = data.suppliers.find((x) => x.id === hero.supplierId) ?? data.suppliers[0];

  const [idx, setIdx] = useState(0);
  const [maxReached, setMax] = useState(0);
  const goStep = (i: number) => { setIdx(i); setMax((m) => Math.max(m, i)); };
  const next = () => goStep(Math.min(idx + 1, 6));

  // step state
  const [supplierId, setSupplierId] = useState<string>(hero.supplierId);
  const [carrier, setCarrier] = useState<string>(copy.carrier);

  const [uploads, setUploads] = useState<Record<string, boolean>>({});
  const up = (k: string) => setUploads((u) => ({ ...u, [k]: true }));

  const [confirmed, setConfirmed] = useState<Set<string>>(() => new Set<string>());

  const [ran, setRan] = useState(false);
  const [pipeDone, setPipeDone] = useState(false);
  const reduce =
    typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const runPipeline = () => {
    setRan(true);
    setPipeDone(false);
    setTimeout(() => setPipeDone(true), reduce ? 0 : 1300);
  };
  const verifyDone = pipeDone;

  // professional request
  const [proKind, setProKind] = useState<string | null>(null);
  const [proCountry, setProCountry] = useState<string>(
    heroSupplier && COUNTRY[heroSupplier.jurisdiction] ? heroSupplier.jurisdiction : 'GB',
  );
  const [proNeedsXl, setProNeedsXl] = useState(false);
  const [proPresented, setProPresented] = useState(false);

  const [decision, setDecision] = useState<DecisionKey | null>(null);
  const [rationale, setRationale] = useState('');
  const [signoff, setSignoff] = useState(false);
  const [issued, setIssued] = useState(false);

  const s = data.suppliers.find((x) => x.id === supplierId) ?? data.suppliers[0];
  const doc = inbox.find((d) => d.supplierId === hero.supplierId) ?? inbox[0];

  if (!s || !doc) {
    return <StateBox kind="empty" title="No supplier or document available for this vertical" />;
  }

  const cstate = carrierState(carrier);

  const lowKeys = doc.fields.filter((f) => f.low || f.conf < 80).map((f) => f.k);
  const allConfirmed = lowKeys.every((k) => confirmed.has(k));

  // professional derivations
  const pro = proKind ? data.professionals.find((p) => p.kind === proKind) : undefined;
  const xl = data.professionals.find((p) => p.kind === 'translator');
  // a notary act on a foreign-language document triggers a certified translation first
  const proItems: Professional[] = pro
    ? proKind === 'notary' && proNeedsXl && xl
      ? [pro, xl]
      : [pro]
    : [];
  const proTotal = proItems.reduce((a, p) => a + proCost(p.cost, proCountry), 0);

  const recommended =
    doc.status === 'translate' || /arabic/i.test(doc.kind)
      ? 'translator'
      : ['AE', 'JO', 'TR'].includes(s.jurisdiction)
        ? 'notary'
        : 'trust lawyer';
  const recReason =
    recommended === 'translator'
      ? 'a submitted document is in a foreign language'
      : recommended === 'notary'
        ? 'free-zone control needs a notarised attestation'
        : 'beneficial ownership needs a legal opinion';
  const recPro = data.professionals.find((p) => p.kind === recommended);
  const recCountry = COUNTRY[s.jurisdiction] ? s.jurisdiction : 'GB';
  const recCountryReason =
    proKind === 'translator' ? 'where the source document originates' : "where the supplier's entity and documents sit";

  const needs2 = s.tier === 'critical';
  const canDecide = decision !== null && rationale.trim().length > 10 && (!needs2 || signoff);

  const decisionOpts: ReadonlyArray<[DecisionKey, string, string]> = [
    ['qualify', config.decision_wording.qualify, 'all required checks clear or attested'],
    ['conditional', config.decision_wording.qualify_with_conditions, copy.conditionalDesc],
    ['block', config.decision_wording.block, 'do not proceed until the flag resolves'],
  ];

  return (
    <div className="screen-inner" style={{ maxWidth: 880, margin: '0 auto' }}>
      <ScreenHeader
        eyebrow="§ Start a diligence"
        title="Qualify a supplier, end to end"
        style={{ marginBottom: 18 }}
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <TierBadge level={s.tier} sm />
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--ink)', fontWeight: 500 }}>{s.name}</span>
          </div>
        }
      />

      <Rail idx={idx} maxReached={maxReached} go={goStep} />

      {/* STEP 0 · START */}
      {idx === 0 && (
        <Card>
          <StepTitle n="1" title="Start the claim" hint="A structured assertion about a supplier and the part it sells. The unit carrier parses live; a malformed component is named before any network call." />
          <div style={{ display: 'grid', gap: 14 }}>
            <Field label="Supplier">
              <Select
                value={supplierId}
                onChange={setSupplierId}
                ariaLabel="Supplier"
                options={data.suppliers.map((x) => ({ value: x.id, label: x.name + ' · ' + x.flag + ' ' + x.jurisdiction }))}
              />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label={copy.partLabel}><Input mono defaultValue={s.part} /></Field>
              <Field label={config.identity.identifier_label}><Input mono defaultValue={copy.identityValue} /></Field>
            </div>
            <Field
              label={copy.carrierLabel}
              hint={cstate.ok ? 'parses clean · ' + cstate.note + ' · ' + copy.carrierHintOk : 'malformed: ' + cstate.note}
              hintTone={cstate.ok ? 'ok' : 'err'}
            >
              <Input mono tone={cstate.ok ? 'ok' : 'err'} value={carrier} onChange={(e) => setCarrier(e.target.value)} spellCheck={false} />
            </Field>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--muted)' }}>consignment · {s.consignment}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 20, alignItems: 'center' }}>
            <Button variant="primary" iconRight="arrow" disabled={!cstate.ok} onClick={next}>Continue to documents</Button>
            {!cstate.ok && <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--flag-ink)' }}>resolve the carrier to continue</span>}
          </div>
        </Card>
      )}

      {/* STEP 1 · DOCUMENTS (upload) */}
      {idx === 1 && (
        <Card>
          <StepTitle n="2" title="Upload the documents" hint="Documents enter through the OCR front door. Drop the type approval, test report, and custody chain — Garnet extracts the fields and you confirm them next." />
          <div style={{ display: 'grid', gap: 10 }}>
            {copy.flowDocs.map(([k, label, sub, req]) => (
              <div
                key={k}
                onClick={() => up(k)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && up(k)}
                style={{
                  border: '1px dashed ' + (uploads[k] ? 'var(--verified)' : 'var(--rule)'), borderRadius: 'var(--radius-md)', padding: '16px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 12, background: uploads[k] ? 'var(--verified-bg)' : 'var(--panel)',
                }}
              >
                <span style={{ color: uploads[k] ? 'var(--verified-ink)' : 'var(--muted)' }}><Icon name={uploads[k] ? 'check' : 'upload'} size={18} /></span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 500 }}>{label}{req && <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--flag-ink)', marginLeft: 8 }}>required</span>}</div>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{uploads[k] ? 'uploaded · queued for OCR' : sub}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 20, alignItems: 'center' }}>
            <Button variant="secondary" icon="chevron" onClick={() => goStep(0)} style={{ transform: 'none' }}>Back</Button>
            <Button variant="primary" iconRight="arrow" disabled={!uploads.approval} onClick={next}>Continue to OCR review</Button>
            {!uploads.approval && <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>the type approval is required</span>}
          </div>
        </Card>
      )}

      {/* STEP 2 · OCR REVIEW */}
      {idx === 2 && (
        <Card>
          <StepTitle n="3" title="Confirm the extracted fields" hint="No extracted field is trusted until a human confirms it. Fields below the confidence threshold carry a gold confirm — they need attestation, not a guess." />
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>{doc.id} · {doc.kind}</div>
          <div style={{ borderTop: '1px solid var(--ink)' }}>
            {doc.fields.map((f) => {
              const low = f.low || f.conf < 80;
              const done = confirmed.has(f.k);
              return (
                <div key={f.k} style={{ display: 'grid', gridTemplateColumns: '150px 1fr auto auto', gap: 14, alignItems: 'center', padding: '11px 0', borderBottom: '1px solid var(--rule)' }}>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--muted)' }}>{f.k}</span>
                  <span style={{ fontFamily: f.code ? 'var(--mono)' : 'var(--body)', fontSize: f.code ? 13.5 : 14, fontWeight: f.code ? 600 : 400, color: f.v === '—' ? 'var(--muted)' : 'var(--ink)' }}>{f.v}</span>
                  <OCRConfidence conf={f.conf} />
                  <span style={{ minWidth: 96, display: 'flex', justifyContent: 'flex-end' }}>
                    {low ? (
                      <ConfirmButton done={done} onClick={() => setConfirmed((c) => new Set(c).add(f.k))} />
                    ) : (
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--muted)' }}>claim · verify</span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 20, alignItems: 'center' }}>
            <Button variant="secondary" icon="chevron" onClick={() => goStep(1)}>Back</Button>
            <Button variant="primary" iconRight="arrow" disabled={!allConfirmed} onClick={next}>Continue to verify</Button>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: allConfirmed ? 'var(--verified-ink)' : 'var(--muted)' }}>
              {allConfirmed
                ? 'all low-confidence fields confirmed'
                : lowKeys.length - [...confirmed].filter((k) => lowKeys.includes(k)).length + ' field(s) await confirmation'}
            </span>
          </div>
        </Card>
      )}

      {/* STEP 3 · VERIFY */}
      {idx === 3 && (
        <Card max={820}>
          <StepTitle n="4" title="Run the verification pipeline" hint="The engine plans the checks for this part and jurisdiction, runs the deterministic ones, and opens attestation where machine sources stop." />
          {!ran ? (
            <div style={{ border: '1px dashed var(--rule)', borderRadius: 'var(--radius-md)', padding: '28px', textAlign: 'center' }}>
              <Button variant="primary" icon="refresh" onClick={runPipeline}>Run pipeline · {hero.checks.length} checks</Button>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)', marginTop: 10 }}>entity · asset · document layers</div>
            </div>
          ) : (
            <div className="panel">
              {hero.checks.map((c, i) => (
                <div
                  key={c.id}
                  style={{
                    display: 'grid', gridTemplateColumns: '44px 1fr auto', gap: 12, alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid var(--rule)',
                    animation: reduce ? undefined : 'fadeup .3s ease both', animationDelay: i * 55 + 'ms',
                  }}
                >
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 600, color: 'var(--muted)' }}>{c.id}</span>
                  <div><span style={{ fontSize: 13, fontWeight: 500 }}>{c.name}</span><span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)', marginLeft: 8 }}>{c.source}</span></div>
                  <StatePill state={c.state} />
                </div>
              ))}
              {!pipeDone && (
                <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10, color: 'var(--muted)' }}>
                  <span style={{ width: 14, height: 14, border: '2px solid var(--rule)', borderTopColor: 'var(--ink)', borderRadius: '50%', display: 'inline-block', animation: 'spin .7s linear infinite' }} />
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>running checks…</span>
                </div>
              )}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 20, alignItems: 'center' }}>
            <Button variant="secondary" icon="chevron" onClick={() => goStep(2)}>Back</Button>
            <Button variant="primary" iconRight="arrow" disabled={!verifyDone} onClick={next}>Continue to professional request</Button>
            {verifyDone && <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--attest-ink)' }}>composited · 1 flag · weakest link named</span>}
          </div>
        </Card>
      )}

      {/* STEP 4 · PROFESSIONAL REQUEST */}
      {idx === 4 && (
        <Card>
          <StepTitle n="5" title="Raise a professional request" hint="Some checks need a regulated professional act. Each carries a preset cost that varies by country and resolves to gold — verified by attestation. A notary on a foreign-language document triggers a certified translation first." />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--muted)' }}>Choose the act</span>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--attest-ink)', background: 'var(--attest-bg)', border: '1px solid var(--attest)', borderRadius: 3, padding: '1px 7px' }}>
              recommended · {(recPro?.label ?? recommended).toLowerCase()} — {recReason}
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
            {data.professionals.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => { setProKind(p.kind); setProCountry(recCountry); setProPresented(false); }}
                aria-pressed={proKind === p.kind}
                style={{
                  textAlign: 'left', cursor: 'pointer', padding: '14px 15px', borderRadius: 'var(--radius-md)', position: 'relative',
                  border: '1px solid ' + (proKind === p.kind ? 'var(--ink)' : p.kind === recommended ? 'var(--attest)' : 'var(--rule)'), background: proKind === p.kind ? 'var(--paper)' : 'var(--panel)',
                }}
              >
                {p.kind === recommended && (
                  <span style={{ position: 'absolute', top: -8, left: 12, fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--attest-ink)', background: 'var(--attest-bg)', border: '1px solid var(--attest)', borderRadius: 2, padding: '1px 6px' }}>recommended</span>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontFamily: 'var(--display)', fontWeight: 600, fontSize: 14 }}>{p.label}</span>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 600 }}>€{proCost(p.cost, proCountry)}</span>
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 5, lineHeight: 1.4 }}>{p.desc}</div>
              </button>
            ))}
          </div>

          {proKind && (
            <div style={{ marginTop: 14, maxWidth: 420 }}>
              <Field
                label="Country · routed through Garnet's in-country network"
                hint={proCountry === recCountry ? 'recommended · ' + recCountryReason : 'recommended country is ' + COUNTRY[recCountry]}
                {...(proCountry === recCountry ? { hintTone: 'ok' as const } : {})}
              >
                <Select
                  value={proCountry}
                  onChange={(v) => { setProCountry(v); setProPresented(false); }}
                  ariaLabel="Country"
                  options={COUNTRY_KEYS.map((c) => ({ value: c, label: c + ' · ' + COUNTRY[c] + (c === recCountry ? ' · recommended' : '') }))}
                />
              </Field>
              {proCountry !== recCountry && (
                <div style={{ marginTop: 8 }}>
                  <Button sm variant="secondary" icon="check" onClick={() => { setProCountry(recCountry); setProPresented(false); }}>Use recommended · {COUNTRY[recCountry]}</Button>
                </div>
              )}
            </div>
          )}

          {proKind === 'notary' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 14, fontFamily: 'var(--mono)', fontSize: 12.5 }}>
              <input type="checkbox" checked={proNeedsXl} onChange={(e) => { setProNeedsXl(e.target.checked); setProPresented(false); }} style={{ width: 16, height: 16 }} />
              Document is in a foreign language — the notary requires a certified translation first
            </label>
          )}

          {/* presented request */}
          {proKind && (
            <div className="panel" style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '11px 16px', borderBottom: '1px solid var(--ink)', fontFamily: 'var(--mono)', fontSize: 10.5, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--muted)' }}>
                <span>presented request</span><span>preset cost</span>
              </div>
              {proItems.map((p) => {
                const a = (data.assignees[p.kind] ?? [])[0];
                const triggered = p.kind === 'translator' && proKind === 'notary';
                return (
                  <div key={p.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--rule)' }}>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 13.5 }}>{p.label}{triggered && <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--attest-ink)', background: 'var(--attest-bg)', border: '1px solid var(--attest)', borderRadius: 2, padding: '1px 5px', marginLeft: 8 }}>triggered by notary</span>}</div>
                      <div style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--muted)', marginTop: 3 }}>{a ? a.firm + ' · ' + COUNTRY[proCountry] + ' · SLA ' + a.sla : 'Garnet in-country network · ' + COUNTRY[proCountry]}</div>
                    </div>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 14, fontWeight: 600 }}>€{proCost(p.cost, proCountry)}</span>
                  </div>
                );
              })}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px' }}>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--muted)' }}>Total · resolves to attestation</span>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 18, fontWeight: 600 }}>€{proTotal}</span>
              </div>
            </div>
          )}

          {proPresented && (
            <div className="weak" style={{ marginTop: 14, borderLeftColor: 'var(--verified)', background: 'var(--verified-bg)' }}>
              <span className="wl" style={{ color: 'var(--verified-ink)' }}>sent</span>
              <span className="wt">
                Request presented · €{proTotal} · {proItems.map((p) => p.label).join(' + ')}. {proKind === 'notary' && proNeedsXl ? 'The certified translation runs first, then the notary act; both resolve to gold.' : 'Resolves to verified · attestation (gold).'}
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  <Button sm variant="secondary" icon="link" onClick={() => go('professional')}>Track in professional requests</Button>
                  <Button sm variant="ghost" icon="eye" onClick={() => go('dash')}>Open dashboard</Button>
                </div>
              </span>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 20, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button variant="secondary" icon="chevron" onClick={() => goStep(3)}>Back</Button>
            {proKind && !proPresented && <Button variant="primary" icon="link" onClick={() => setProPresented(true)}>Present request · €{proTotal}</Button>}
            <Button variant={proKind ? 'ghost' : 'primary'} iconRight="arrow" onClick={next}>{proKind ? 'Continue to record' : 'No professional act needed · continue'}</Button>
          </div>
        </Card>
      )}

      {/* STEP 5 · RECORD */}
      {idx === 5 && (
        <div style={{ maxWidth: 820 }}>
          <Card max={820}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
              <div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>{hero.recordId} · {s.id}</div>
                <h2 className="display" style={{ fontSize: '1.45rem', fontWeight: 600, margin: '4px 0 0' }}>{s.name}</h2>
              </div>
              <div style={{ display: 'flex', gap: 9, alignItems: 'center' }}><QualState state={s.state} /><TierBadge level={s.tier} /></div>
            </div>
            <div style={{ marginBottom: 14 }}><DepthGradient reached={toDepth(hero.depthReached)} /></div>
            <WeakestLinkBanner tone={hero.weakest.state === 'flag' ? 'flag' : 'attest'}>
              <b>{hero.weakest.checkId} · unit authenticity.</b> {hero.weakest.text}
            </WeakestLinkBanner>
            <div style={{ marginTop: 14 }} className="panel">
              <div style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--muted)', padding: '10px 14px', borderBottom: '1px solid var(--ink)' }}>Per-check breakdown</div>
              {hero.checks.map((c, i) => (
                <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '44px 1fr auto', gap: 10, alignItems: 'center', padding: '9px 14px', borderBottom: i < hero.checks.length - 1 ? '1px solid var(--rule)' : 0 }}>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 600, color: 'var(--muted)' }}>{c.id}</span>
                  <span style={{ fontSize: 13 }}>{c.name}</span>
                  <StatePill state={c.state} />
                </div>
              ))}
            </div>
            <div style={{ marginTop: 14 }}><RenewalTimeline name={s.nextLapse.name} meta={'expiring · ' + s.nextLapse.days + ' days'} state="expiring" pct={78} /></div>
            {proPresented && proItems.length > 0 && (
              <div className="weak" style={{ marginTop: 14, borderLeftColor: 'var(--attest)', background: 'var(--attest-bg)' }}>
                <span className="wl">attest</span>
                <span className="wt">Professional request attached · {proItems.map((p) => p.label).join(' + ')} · €{proTotal} · resolves to verified · attestation.</span>
              </div>
            )}
          </Card>
          <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
            <Button variant="secondary" icon="chevron" onClick={() => goStep(4)}>Back</Button>
            <Button sm variant="secondary" icon="download">Export</Button>
            <Button sm variant="secondary" icon="share">Share</Button>
            <Button variant="primary" iconRight="arrow" onClick={next}>Continue to decision</Button>
          </div>
        </div>
      )}

      {/* STEP 6 · DECISION */}
      {idx === 6 && (
        <Card>
          <StepTitle n="7" title="Record the procurement decision" hint="The record states how far trust was established and names the weakest link. The buyer decides — qualify, qualify with conditions, or block — and the decision is signed into the record." />
          {issued ? (
            <div>
              <div className="weak" style={{ borderLeftColor: decision === 'block' ? 'var(--flag)' : 'var(--verified)', background: decision === 'block' ? 'var(--flag-bg)' : 'var(--verified-bg)' }}>
                <span className="wl" style={{ color: decision === 'block' ? 'var(--flag-ink)' : 'var(--verified-ink)' }}>{decision === 'block' ? 'blocked' : 'decided'}</span>
                <span className="wt">
                  <b>{decision === 'qualify' ? 'Qualified' : decision === 'conditional' ? 'Qualified with conditions' : 'Blocked'}</b> · S. Lench · compliance approver · 2026-06-11<br />{rationale}<br />
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>record {hero.recordId} superseded → v4 · decision signed in</span>
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
                <Button variant="primary" icon="file" onClick={() => go('record')}>Open the record</Button>
                <Button variant="secondary" icon="eye" onClick={() => go('dash')}>Back to dashboard</Button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
                {decisionOpts.map(([k, label, d]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setDecision(k)}
                    aria-pressed={decision === k}
                    style={{
                      textAlign: 'left', cursor: 'pointer', padding: '13px 14px', borderRadius: 'var(--radius-md)',
                      border: '1px solid ' + (decision === k ? 'var(--ink)' : 'var(--rule)'), background: decision === k ? 'var(--paper)' : 'var(--panel)',
                    }}
                  >
                    <div style={{ fontFamily: 'var(--display)', fontWeight: 600, fontSize: 14 }}>{label}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 4, lineHeight: 1.4 }}>{d}</div>
                  </button>
                ))}
              </div>
              <Field label="Rationale">
                <textarea
                  className="input"
                  rows={3}
                  value={rationale}
                  onChange={(e) => setRationale(e.target.value)}
                  placeholder="State the decision and the evidence it rests on."
                  style={{ resize: 'vertical', fontFamily: 'var(--body)' }}
                />
              </Field>
              {needs2 && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontFamily: 'var(--mono)', fontSize: 12.5 }}>
                  <input type="checkbox" checked={signoff} onChange={(e) => setSignoff(e.target.checked)} style={{ width: 16, height: 16 }} />
                  Second compliance sign-off · required for a critical-tier supplier
                </label>
              )}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Button variant="secondary" icon="chevron" onClick={() => goStep(5)}>Back</Button>
                <Button variant="primary" icon="check" disabled={!canDecide} onClick={() => setIssued(true)}>Sign decision into record</Button>
                {!canDecide && (
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>
                    {!decision ? 'choose a decision' : rationale.trim().length <= 10 ? 'add a rationale' : 'second sign-off required'}
                  </span>
                )}
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
